import { notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, useFragment, useQueryLoader } from "react-relay";

import { article, data } from "~/data";
import type { morningStudioArticleFooterContent$key } from "~/queries/__generated__/morningStudioArticleFooterContent.graphql";
import type { morningStudioArticleFooterQuery } from "~/queries/__generated__/morningStudioArticleFooterQuery.graphql";

import { MorningStudioArticleFooterDisplay } from "./morning-studio-article-footer-display";

export type Props = {
  className?: string;
  reference: morningStudioArticleFooterContent$key;
};

export const query = graphql`
  query morningStudioArticleFooterQuery(
    $applicationId: String!
    $articleTypeId: String!
    $entityUuid: String!
    $topicEntityUuid: String
    $withTopic: Boolean!
  ) {
    ...morningStudioArticleFooterDisplayQuery
      @arguments(
        applicationId: $applicationId
        articleTypeId: $articleTypeId
        entityUuid: $entityUuid
        topicEntityUuid: $topicEntityUuid
        withTopic: $withTopic
      )
  }
`;

export const MorningStudioArticleFooter: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const content = useFragment(
    graphql`
      fragment morningStudioArticleFooterContent on Content {
        entityUuid
        topics {
          entityUuid
        }
      }
    `,
    reference_,
  );
  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<morningStudioArticleFooterQuery>(query);

  useEffect(() => {
    if (!content.entityUuid) return;

    const firstTopic = first(content.topics);

    loadQuery({
      applicationId: data.application.scmp.entityUuid,
      articleTypeId: article.types.studio.entityId,
      entityUuid: content.entityUuid,
      topicEntityUuid: firstTopic?.entityUuid,
      withTopic: notEmpty(firstTopic),
    });

    return disposeQuery;
  }, [content?.entityUuid, content?.topics, disposeQuery, loadQuery]);

  return queryReference ? (
    <MorningStudioArticleFooterDisplay className={className} queryReference={queryReference} />
  ) : null;
};

MorningStudioArticleFooter.displayName = "MorningStudioArticleFooter";
